import { produce } from 'immer';

import {
  OmniElement,
  OmniStyleElement,
  html,
  css,
  ref,
  createRef,
  classMap,
  nothing,
} from 'omni-ui';

import './persona-base-card.js';

import { select, min, max, scaleSqrt } from 'd3';
import { cloud } from '../../third-party/d3-cloud.js';

OmniStyleElement.register();

/**
 * @summary Class for PersonaWordCloud
 * @description take in a string and returns a word cloud. It first removes all non alphanumeric characters, then removes any stop words found  within the array{@link #stopWords}, calculates the frequency of each word, and creates a wordDictionary with all the data needed by {@link #_cloudLayout} and {@link #_drawWordCloud},
 * @extends OmniElement
 */
export class PersonaWordCloud extends OmniElement {
  /**
   * @property {Boolean} isEditView - tracks which view PersonaWordCloud is on
   * @property {Object} cloudData - State for entire class: {
      cardType: 'word-cloud',
      content: '',
      wordDictionary: '',
      title: 'Word Cloud',
    }
   * @property {String} title - string of the card's title
   * @property {String} content - string containing orginal string passed in by user
   * @property {Boolean} readOnly - Boolean determining if content is readOnly or editable
   * @property {Array} wordDictionary - array of objects, with each oject containing a specific unique word's text, frquency, font weight and font size
   * @property {Array} stopWords - array containing strings of stop words
   * @property {Array} visualizationColors - array containing the color variables generated by the wordCloud
   */
  static get properties() {
    return {
      isEditView: { type: Boolean },
      readOnly: { type: Boolean },
    };
  }

  /**
   * PersonaWordCloud module styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        :host,
        omni-style.omni,
        .omni peronsa-base-card {
          display: flex;
          flex: 1 1 auto;
          min-height: 18.07rem;
        }

        .content-container {
          display: flex;
          overflow: clip;
          flex: 1 1 auto;
          min-height: 0;
          justify-content: center;
          align-items: center;
          text-align: center;
        }

        svg {
          width: 100%;
          height: 100%;
        }

        .edit-view::part(base-card-container) {
          min-width: 30.29rem;
          height: 30.71rem;
        }

        div.field > label.label {
          font-size: 0.79rem;
          color: var(--color-almost-black);
        }

        .omni div.field.input-container {
          margin-bottom: 2.14rem;
        }

        .omni div.field {
          display: flex;
          flex-flow: column nowrap;
          flex: 1 1 auto;
          min-height: 0;
          margin-bottom: 1.71rem;
        }

        .omni textarea {
          width: 100%;
          height: 14.29rem;
          padding: 1.07rem 1.43rem;
          resize: none;
        }

        .omni textarea:focus-visible {
          outline: solid thin var(--color-primary);
        }
      `,
    ];
  }

  /**
   * Initializes PersonaWordCloud and {@link #isEditView}, {@link #readOnly} {@link #title}, {@link #content}, {@link #wordDictionary}, {@link #stopWords} and {@link #visualizationColors}.
   */
  constructor() {
    super();
    this.omniStyleRef = createRef();
    this.isEditView = false;
    this.readOnly = false;
    this.title = '';
    this.content = '';
    this.wordDictionary = '';

    // prettier-ignore
    this.stopWords = [
      'a', 'also', 'may', 'well', 'about', 'above', 'after', 'again', 'against', 'all', 'am', 'an', 'and', 'any', 'are', "aren't", 'as', 'at', 'be', 'because', 'been', 'before', 'being', 'below', 'between', 'both', 'but', 'by', "can't", 'cannot', 'could', "couldn't", 'did', "didn't", 'do', 'does', "doesn't", 'doing', "don't", 'down', 'during', 'each', 'few', 'for', 'from', 'further', 'had', "hadn't", 'has', "hasn't", 'have', "haven't", 'having', 'he', "he'd", "he'll", "he's", 'her', 'here', "here's", 'hers', 'herself', 'him', 'himself', 'his', 'how', "how's", 'i', "i'd", "i'll", "i'm", "i've", 'if', 'in', 'into', 'is', "isn't", 'it', "it's", 'its', 'itself', "let's", 'me', 'more', 'most', "mustn't", 'my', 'myself', 'no', 'nor', 'not', 'of', 'off', 'on', 'once', 'only', 'or', 'other', 'ought', 'our', 'ours', 'ourselves', 'out', 'over', 'own', 'same', "shan't", 'she', "she'd", "she'll", "she's", 'should', "shouldn't", 'so', 'some', 'such', 'than', 'that', "that's", 'the', 'their', 'theirs', 'them', 'themselves', 'then', 'there', "there's", 'these', 'they', "they'd", "they'll", "they're", "they've", 'this', 'those', 'through', 'to', 'too', 'under', 'until', 'up', 'very', 'was', "wasn't", 'we', "we'd", "we'll", "we're", "we've", 'were', "weren't", 'what', "what's", 'when', "when's", 'where', "where's", 'which', 'while', 'who', "who's", 'whom', 'why', "why's", 'with', "won't", 'would', "wouldn't", 'you', "you'd", "you'll", "you're", "you've", 'your', 'yours', 'yourself', 'yourselves',
    ];
    this.visualizationColors = [
      'var(--color-visualization-mid-blue)',
      'var(--color-visualization-lilac)',
      'var(--color-visualization-periwinkle)',
      'var(--color-visualization-deep-coral)',
      'var(--color-visualization-light-blue)',
      'var(--color-visualization-light-orange)',
      'var(--color-visualization-rose)',
    ];
  }

  /**
   * Sets the state of {@link #title}, {@link #content}, and {@link #wordDictionary} properties to the values in {@link #cloudData} object
   */
  connectedCallback() {
    super.connectedCallback();
    if (this.cloudData) {
      this.title = this.cloudData?.title;
      this.content = this.cloudData?.content;
      this.wordDictionary = this.cloudData?.wordDictionary;
    }
  }

  /**
   * if {@link #wordDictionary} is passed down by parent it invokes {@link #_cloudLayout} with {@link #wordDictionary}
   */
  firstUpdated() {
    if (this.wordDictionary) this.#cloudLayout(this.wordDictionary);
  }

  /**
   *
   * @param {Array} data - array of objects with each object representing a word and all the properites of that word needed for display
   * @summary Starts the layout algorithm. This initialises various attributes on the word objects, and attempts to place each word, starting with the largest word. Starting with the centre of the rectangular area, each word is tested for collisions with all previously-placed words. If a collision is found, it tries to place the word in a new position along the spiral. It then appends the words onto a SVG by invoking {@link #_drawWordCloud} when layout finish on end.
   */
  #cloudLayout(data) {
    const computedStyle = getComputedStyle(this.omniStyleRef.value);

    this.layout = cloud()
      .size([400, 200])
      .words(structuredClone(data)) // "unfrozen" copy of data
      .padding(3)
      .rotate(0)
      .font(computedStyle.getPropertyValue('font-family'))
      .fontSize(d => d.size)
      .on('end', this.#drawWordCloud);
    this.layout.start();
  }

  /**
   * @param {Array} words - array of objects with each object representing a word and all the properites of that word needed for display. Passed by {@link #_cloudLayout} which added additional properties pertaining to x/y positioning.
   * @summary function takes the output of {@link #_cloudLayout} above and appends the words to the svg it appends onto a DOM element.
   */
  #drawWordCloud = words => {
    const container = this.shadowRoot.querySelector('.content-container');
    const computedStyle = getComputedStyle(this.omniStyleRef.value);
    const [width, height] = this.layout.size();

    select(container)
      .append('svg')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`)
      .selectAll('text')
      .data(words)
      .enter()
      .append('text')
      .text(d => d.text)
      .style('font-family', computedStyle.getPropertyValue('font-family'))
      .style(
        'fill',
        () =>
          this.visualizationColors[
            Math.floor(Math.random() * this.visualizationColors.length)
          ],
      )
      .style('font-size', d => `${d.size}px`)
      .style('font-weight', d => d.fontWeight)
      .attr('text-anchor', 'middle')
      .attr('transform', d => `translate(${[d.x, d.y]}) rotate(${d.rotate})`);
  };

  /**
   * @summary function that takes user's text input {@link #content}and removes all non alphanumeric characters, calculates the frequency of each unique non-stop word, and creates a wordDictionary with all the data needed by {@link #_cloudLayout} and {@link #_drawWordCloud}
   */
  #wordFrequency() {
    // remove non alphanumerics, whitespace, and capitalized words
    const regex = /\b[^\d\W]+\b/g;
    const string = this.content?.trim().toLowerCase();
    const sanitizedString = string.match(regex);

    // gets frequency of all non-stop words and return up to the top 40 in an array
    let wordFrequency = [
      ...Object.entries(
        sanitizedString.reduce((dictionary, word) => {
          if (this.stopWords.indexOf(word) === -1 && word.length > 1) {
            // eslint-disable-next-line no-param-reassign
            dictionary[word] = (dictionary[word] || 0) + 1;
          }
          return dictionary;
        }, {}),
        // eslint-disable-next-line no-unused-vars
      ).sort(([_a, a], [_b, b]) => b - a),
    ];
    if (wordFrequency.length > 40) {
      wordFrequency = wordFrequency.slice(0, 40);
    }

    const textStyle = [
      [14, '600'],
      [16, '400'],
      [24, '400'],
      [28, '700'],
      [36, '900'],
    ];

    // scale function to scale list of 40 words againts a range set by textStyle
    const scale = scaleSqrt()
      .domain([min(wordFrequency, x => x[1]), max(wordFrequency, x => x[1])])
      .rangeRound([0, textStyle.length - 1]);

    // maps the frequency array and returns an array with added properties by using the scaledFrequency returned by scale as an index to get the correct textStyle
    this.wordDictionary = wordFrequency.map(([text, frequency]) => {
      const scaledFrequency = scale(frequency);
      const [size, fontWeight] = textStyle[scaledFrequency];
      return {
        text,
        frequency,
        size,
        fontWeight,
      };
    });
  }

  /**
   * @summary controls what view the card renders by reassignning {@link #isEditView} to the passed in boolean and calls {@link #_cloudLayout} to render cloud svg when data is present and view is display
   */
  async #setView(isEdit) {
    this.isEditView = isEdit;

    if (this.wordDictionary && !this.isEditView) {
      await this.updateComplete;
      this.#cloudLayout(this.wordDictionary);
    }
  }

  /**
   * @summary updates {@link #cloudData} and dispatches an event with it as the payload. Also runs {@link #_wordFrequency} and {@link #_cloudLayout}
   * @returns updated {@link #wordClouldData}
   */
  #updateCardData() {
    // when user deletes content or submits an empty string
    if (this.content?.trim() === '') {
      this.wordDictionary = '';
      this.cloudData = produce(this.cloudData, draft => {
        draft.content = this.content;
        draft.wordDictionary = this.wordDictionary;
      });
      this.dispatchNewEvent('persona-change', {
        detail: { cardData: this.cloudData },
      });
      this.#setView(false);
      return;
    }

    this.#wordFrequency();

    this.cloudData = produce(this.cloudData, draft => {
      draft.content = this.content;
      draft.wordDictionary = this.wordDictionary;
    });

    this.dispatchNewEvent('persona-change', {
      detail: { cardData: this.cloudData },
    });

    this.#setView(false);
  }

  /**
   * @returns {TemplateResult} html to render display view
   */
  get #displayViewTemplate() {
    return html`
      <div class="content-container">
        ${this.wordDictionary
          ? nothing
          : html`<p>Word cloud has not been added</p>`}
      </div>
    `;
  }

  /**
   * @returns {TemplateResult} html to render edit view
   */
  get #editViewTemplate() {
    return html`
      <div class="field input-container">
        <label class="label">Card title</label>
        <div class="control">
          <input
            disabled
            class="input"
            type="text"
            .value=${this.cloudData?.title}
          />
        </div>
      </div>
      <div class="field text-container">
        <label class="label">Card description</label>
        <div class="control">
          <textarea
            @input=${e => {
              this.content = e.target.value;
            }}
          >
            ${this.content}
          </textarea
          >
        </div>
      </div>
    `;
  }

  /**
   * @returns {TemplateResult} main HTML to be redered on the DOM
   */
  render() {
    return html`
      <omni-style ${ref(this.omniStyleRef)}>
        <persona-base-card
          class=${classMap({ 'edit-view': this.isEditView })}
          .isEditView=${this.isEditView}
          .readOnly=${this.readOnly}
          .cardTitle=${!this.wordDictionary ? this.cloudData?.title : nothing}
          @persona-card-close=${() => {
            this.#setView(false);
          }}
          @persona-card-submit=${this.#updateCardData}
          @persona-card-edit-click=${() => {
            this.#setView(true);
          }}
        >
          ${this.isEditView
            ? this.#editViewTemplate
            : this.#displayViewTemplate}
        </persona-base-card>
      </omni-style>
    `;
  }
}

OmniElement.register('persona-word-cloud', PersonaWordCloud);
